/**
 * Table-of-contents helpers for help-center articles (2026-09-05).
 *
 * Works on the Hygraph RichText AST (`content.raw.children`) so the ids the
 * sidebar links to are computed from the same source `RichTextContainer`
 * renders — the renderer assigns ids to `heading-two` nodes in document order.
 */
export type RichNode = {
  type?: string
  text?: string
  children?: RichNode[]
}

export type RichRaw = { children?: RichNode[] } | undefined

export type ITocItem = { id: string; text: string }

const RELATED_HEADING = /^(documentos relacionados|related documents)$/i

export const textOf = (node: RichNode | undefined): string =>
  typeof node?.text === 'string'
    ? node.text
    : (node?.children ?? []).map(textOf).join('')

/** Stable, accent-free anchor id from a heading's text. */
export const headingId = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/** Every `heading-two` in document order, with the id the renderer will stamp. */
export const headingsOf = (raw: RichRaw): ITocItem[] => {
  const seen = new Map<string, number>()
  return (raw?.children ?? [])
    .filter((n) => n.type === 'heading-two')
    .map((n) => {
      const text = textOf(n).trim()
      const base = headingId(text) || 'seccion'
      const count = seen.get(base) ?? 0
      seen.set(base, count + 1)
      return { text, id: count === 0 ? base : `${base}-${count + 1}` }
    })
}

/**
 * Drop a trailing hand-written "Documentos relacionados" section (heading +
 * the paragraphs after it) — the auto-generated related sidebar already
 * carries those links, so on articles that have it the section is a duplicate.
 * Returns the input untouched when the last heading is anything else or is
 * followed by non-paragraph content.
 */
export const stripTrailingRelated = (raw: RichRaw): RichRaw => {
  const children = raw?.children
  if (!raw || !children?.length) return raw
  let last = -1
  for (let i = children.length - 1; i >= 0; i--) {
    if (/^heading-/.test(children[i].type ?? '')) {
      last = i
      break
    }
  }
  if (last < 0) return raw
  const heading = children[last]
  if (
    heading.type !== 'heading-two' ||
    !RELATED_HEADING.test(textOf(heading).trim())
  )
    return raw
  const tail = children.slice(last + 1)
  if (!tail.every((n) => n.type === 'paragraph')) return raw
  return { ...raw, children: children.slice(0, last) }
}
